/* eslint-disable no-use-before-define */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { GenericService, TagService } from "@services";
import { GOOGLE_MAP_SETTINGS, FORM_JOB_TYPES, DAYS_OF_WEEK } from "@constants";
import utils from "@utils";
import { MAX_JOB_EVENT_ATTACHMENT_SIZE } from "@constants/index";
import AttachmentListItem from "@components/common/AttachmentListItem";
import { ReactSelect, LocationModal, SelectBadges } from "../../../../../components";
import { JobRequestClientSearch, QuickAddProjectModal } from "../modals";
import AddClientModal from "../modals/AddClientModal";
import SecondarySupervisor from "./SecondarySupervisor";
import DateSelector, { timeToHours } from "../DateSelector";
import LocationOption from "./LocationOption";

const emptySecondarySupervisor = {
	SecondaryAgencySupervisorId: "",
	SendNotification: false,
};

const GeneralInformation = props => {
	const {
		user,
		form,
		jobEditingProperties,
		jobRequestProperties,
		validateSection,
		updateForm,
		updateRequestProperties,
		getContacts,
		agencyContacts,
		clientContacts,
		openQuickAddContactModal,
		updateLocationCoordinates,
		getClients,
		clients,
		loadingClients,
		isInvoicingFirstEnabled,
		loadingProjects,
		projects,
		loadingLocations,
		locations,
		getProjects,
		getLocations,
		setLocations,
		onClientChange,
		onJobEventTypeChange,
		isNewJob,
		location,
		flags: { jobDescriptionAttachments },
	} = props;
	const isDraft = Boolean(location?.query?.draftId);
	const { lang } = global;
	const [clientName, setClientName] = useState("");
	const [jobEventTypes, setJobEventTypes] = useState([]);
	const [showLocationModal, setShowLocationModal] = useState(false);
	const [quickAddProjectModalShow, setQuickAddProjectModalShow] = useState(false);
	// Loaders
	const [loadingJobEventTypes, setLoadingJobEventTypes] = useState(false);
	const [showAddClientModal, setShowAddClientModal] = useState(false);
	const [jobTags, setJobTags] = useState([]);
	const [assignedJobtags, setAssignedJobtags] = useState([]);
	const [changeTags, setChangeTags] = useState(false);
	const [attachments, setAttachments] = useState(() => {
		if (form.JobEventAttachments && form.JobEventAttachments.length > 0) {
			return form.JobEventAttachments;
		}
		return [];
	});

	useEffect(()=>{
		if(form.JobEventAttachments && form.JobEventAttachments.length > 0) {
			setAttachments(form.JobEventAttachments);
		}
	}, [form.JobEventAttachments])

	const fileInputRef = useRef(null);
	const _jobRequestClientSearch = useRef();

	useEffect(() => {
		if (!isInvoicingFirstEnabled) {
			getClients();
		}

		getJobEventTypes();

		getTags();
		// FE-1168: Removed get contacts call for LEAs so we don't get agency contacts until a client has been selected for any user
		if (!user.isLEA()) {
			getContacts();
		}
	}, []);

	// keep up to date with section changes (editing, duplicating, drafts, requests)
	useEffect(() => {
		if (jobRequestProperties.showClientModal) {
			_jobRequestClientSearch.current.openModal({
				clientName: jobRequestProperties.clientName ?? "",
			});
		}
		if (form._clientId && clientContacts.length === 0) getContacts(form._clientId);
		if (form._clientId && projects.length === 0 && loadingProjects === false)
			getProjects(form._clientId);
		if (form.ProjectId && locations.length === 0 && loadingLocations === false)
			getLocations(form.ProjectId);
	}, [form]);

	// When the recurrence type changes, we need to revalidate to check for empty inputs
	useEffect(() => {
		validateForm();
	}, [
		form.JobEventRecurrenceRule.EndType,
		form.JobEventRecurrenceRule.CustomDates,
		form._startDate,
		form.SecondarySupervisorModel,
	]);

	useEffect(() => {
		if (form.JobEventTags != null)
			setAssignedJobtags(
				form.JobEventTags.map(item => ({ label: item.Name, value: item.Id })),
			);
	}, [form.JobEventTags]);

	/**
	 * Handles a majority of input changes in the General Information section
	 * @param {String} property The name of the property to update
	 * @param {String} type The type of input used to update the value. Defaults to 'text'.
	 */
	const handleChange =
		(property, type = "text") =>
		event => {
			let value = "";
			if (type === "select") value = event?.value || "";
			else if (type === "text")
				({
					target: { value },
				} = event);
			else if (type === "checkbox")
				({
					target: { checked: value },
				} = event);
			else value = event;
			const {
				JobEventRecurrenceRule: { ...JobEventRecurrenceRule },
				_startDate,
			} = form;
			const updates = { [property]: value, JobEventRecurrenceRule };
			const newEstimate = [
				"_startTime",
				"_endTime",
				"_isRecurring",
				"_flexJob",
				"_flexJobMinHours",
			].includes(property);
			if (property === "_isRecurring" && value === false) {
				// For date inputs on iOS, all '/' characters need to be replaced with '-'
				let StartsOn = utils.isIOSDevice ? _startDate.replace(/\//gi, "-") : _startDate;

				// If the displayed recurrence start date is not mm/dd/yyyy, format it so that it is
				if (utils.isIOSDevice) {
					// For display purposes, we always want to show this date with '/' characters, even on iOS
					StartsOn = StartsOn.replace(/-/gi, "/");
				}
				if (!utils.isDesktopSafari && !utils.isIE) {
					StartsOn = utils.formatDate(new Date(`${StartsOn} 00:00`), "en-US");
				}
				updates.JobEventRecurrenceRule = {
					StartsOn,
					RepeatType: 1,
					RepeatsOn: [],
					EndType: "NEVER",
					EndsAfterOccurrence: "",
					EndsOn: "",
					WeekFrequency: "1",
					CustomDates: [],
				};
			}
			if (property === "_flexJob" && value === false) updates._flexJobMinHours = "";

			updateForm("generalInfo", updates, newEstimate, validateForm);
		};

	const openQuickAddProjectModal = () => {
		setQuickAddProjectModalShow(true);
	};

	const getTags = () => {
		TagService.getTags()
			.then(({ data }) => {
				const items = data.map(item => ({
					value: item.Id,
					label: item.Name,
				}));
				setJobTags(items);
			})
			.catch(() => {
				setJobTags([]);
				utils.notify({
					type: "error",
					message: lang.t("Error loading job tags"),
					timeOut: 1500,
				});
			});
	};

	/** Retrieves the list of job event types that can be selected when creating/updating a job */
	const getJobEventTypes = () => {
		setLoadingJobEventTypes(true);
		GenericService.getJobEventTypes()
			.then(({ data }) => {
				const items = data.map(({ Id: value, Name: label }) => ({ value, label }));
				setJobEventTypes(items);
				setLoadingJobEventTypes(false);
			})
			.catch(() => {
				setJobEventTypes([]);
				setLoadingJobEventTypes(false);
				utils.notify({
					type: "error",
					message: lang.t("Error loading Job Types"),
					timeOut: 1500,
				});
			});
	};

	// Automatically fetch projects + contacts and update paymentDetails when client id is updated
	useEffect(() => {
		if (clients.length > 0 && form._clientId !== "") {
			const client = clients.find(item => item.value === form._clientId);
			setClientName(client?.label);
			if (isInvoicingFirstEnabled) {
				return;
			}
			if (isNewJob && client?.billingContactId) {
				updateForm(
					"paymentDetails",
					{ ClientBillingContactId: client.billingContactId },
					false,
				);
			}
		}
	}, [clients, form._clientId, clientName]);

	// Autofill the project if at least one exists and no project ID is selected
	useEffect(() => {
		if (projects.length > 0 && form._clientId !== "" && form.ProjectId === "") {
			const [{ value: ProjectId }] = projects;
			updateForm("generalInfo", { ProjectId });
			getLocations(ProjectId);
		}
	}, [form._clientId, form.ProjectId, projects]);

	// Autofill the location if one exists and both a client and a project have been selected
	useEffect(() => {
		const validLocations = locations.filter(elm => !elm.isDisabled);
		if (
			form.LocationId === "" &&
			validLocations.length > 0 &&
			form._clientId !== "" &&
			form.ProjectId !== ""
		) {
			const [{ value: LocationId }] = validLocations;
			updateForm("generalInfo", { LocationId });
		}
	}, [form._clientId, form.ProjectId, locations, form.LocationId]);

	/**
	 * Handles changes to the project in this section and updates the HOC with the latest value
	 * @param {Object} event An object with `value` and `label` properties returned by a `ReactSelect` element when the selected value has been changed
	 */
	const onProjectChange = event => {
		if (event?.value === form.ProjectId) return;
		updateForm("generalInfo", { ProjectId: event?.value || "", LocationId: "" });
		if (event) {
			const ProjectId = event.value;
			getLocations(ProjectId);
		} else {
			setLocations([]);
			validateSection("generalInfo", false);
		}
		updateLocationCoordinates(GOOGLE_MAP_SETTINGS.defaultCenter);
	};

	/**
	 * Handles changes to the location in this section and updates the HOC with the latest value.
	 * Values in other form sections that are impacted by location changes are also updated.
	 * @param {Object} event An object with `value` and `label` properties returned by a `ReactSelect` element when the selected value has been changed
	 */
	const onLocationChange = event => {
		if (event?.value === form.LocationId) return;
		updateForm("generalInfo", { LocationId: event?.value || "" });
		if (!event) {
			validateSection("generalInfo", false);
			updateLocationCoordinates(GOOGLE_MAP_SETTINGS.defaultCenter);
		}
	};

	// Automatically update paymentDetails and map coordinates when location id is updated
	useEffect(() => {
		if (form.ProjectId !== "" && locations.length > 0 && form.LocationId !== "") {
			const _location = locations.find(
				item => item.value === form.LocationId && !item.isDisabled,
			);
			if (_location) updateLocationCoordinates(_location.coordinates);
		}
	}, [locations, form.LocationId]);

	/* If we are loading data from a request/draft or editing a job, this component needs to make
	all its updates before the form's loading indicator should be stopped */
	useEffect(() => {
		const { finishLoadingForm, loadingForm } = props;
		const clientUpToDate = form._clientId !== "" && clients.length > 0;
		const projectUpToDate = form.ProjectId !== "" && projects.length > 0;
		const locationUpToDate = form.LocationId !== "" && locations.length > 0;
		if (loadingForm) {
			if (
				(clientUpToDate && projectUpToDate && locationUpToDate) ||
				(clientUpToDate && projectUpToDate) ||
				clientUpToDate
			) {
				finishLoadingForm();
			}
		}
	}, [clients, projects, locations]);

	const addJobTag = ({ target: { value } }) => {
		if (assignedJobtags.length >= 5) {
			utils.notify({
				type: "error",
				message: lang.t("Cannot assign more than 5 tags to a job"),
				timeOut: 1500,
			});
			return;
		}
		const auxJobTags = [...assignedJobtags];
		const newJobTag = jobTags.find(item => Number(value) === item.value);
		const jobTagsAux = [...auxJobTags, newJobTag].map(item => ({
			Id: item.value,
			Name: item.label,
			OrganizationId: user.Organization.Id,
		}));
		updateForm("generalInfo", { JobEventTags: jobTagsAux });
	};

	const onRemoveTag = tag => {
		const auxJobTags = assignedJobtags.filter(item => item.value !== tag);
		const jobTagsAux = auxJobTags.map(item => ({
			Id: item.value,
			Name: item.label,
			OrganizationId: user.Organization.Id,
		}));
		updateForm("generalInfo", { JobEventTags: jobTagsAux });
	};

	const handleFileInputClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleFileSelection = event => {
		const files = Array.from(event.target.files);
		if (files.length > 0) {
			/**
			 * Ensure we have only one file selected
			 */
			const file = files[0];
			const newAttachment = {
				id: Math.random().toString(36).substring(2, 9),
				file,
				name: file.name,
				size: file.size,
				type: file.type,
				existingFile: false,
			};

			const updatedAttachments = [newAttachment];
			setAttachments(updatedAttachments);

			// Update the form state to include the attachments
			updateForm("generalInfo", { JobEventAttachments: updatedAttachments });

			// Reset the file input so the same file can be selected again if needed
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	const handleRemoveFile = attachmentId => {
		const updatedAttachments = attachments.filter(attachment => attachment.id !== attachmentId);
		setAttachments(updatedAttachments);

		// Update the form state to remove the attachment
		updateForm("generalInfo", { JobEventAttachments: updatedAttachments });
	};

	const onIncludeCoordinatorsChange = event => {
		const value = event.target.checked;
		const updates = {
			_includeCoordinators: value,
			SupervisorContactId: "",
		};

		updateForm("generalInfo", updates);
		getContacts(undefined, value);
	};

	const handleAgencySupervisorChange = event => {
		updateForm("generalInfo", { SupervisorContactId: event?.value || "" }, false, validateForm);
	};

	const handleAddSecondarySupervisor = () => {
		const SecondarySupervisorModel = [
			...form.SecondarySupervisorModel,
			{ ...emptySecondarySupervisor },
		];
		updateForm("generalInfo", { SecondarySupervisorModel }, false, validateForm);
	};

	const updateSecondarySupervisorModel = SecondarySupervisorModel => {
		updateForm("generalInfo", { SecondarySupervisorModel }, false, validateForm);
	};

	const handleContinue = () => {
		const { expandNextSection } = props;
		validateForm();
		updateForm("generalInfo", form, true);
		expandNextSection("generalInfo");
	};

	const closeQuickAddProjectModal = () => {
		setQuickAddProjectModalShow(false);
	};

	/**
	 * Validates all inputs in this section have been properly filled out and no required inputs have been missed
	 * @param {Object} data An object containing general information properties and values to use instead of what is currently passed via `this.props.form`
	 * @returns {Boolean} whether this section of the job form is valid or not
	 */
	const validateForm = data => {
		let {
			Name,
			ProjectId,
			LocationId,
			JobEventTypeId,
			Details,
			_startDate,
			_startTime,
			_endTime,
			_isRecurring,
			_flexJob,
			_flexJobMinHours,
			JobEventRecurrenceRule: {
				RepeatType,
				RepeatsOn,
				EndType,
				EndsAfterOccurrence,
				EndsOn,
				WeekFrequency,
				CustomDates,
			},
			SupervisorContactId,
			SecondarySupervisorModel,
		} = form;
		if (data) {
			({
				generalInfo: {
					Name,
					ProjectId,
					LocationId,
					JobEventTypeId,
					Details,
					_startDate,
					_startTime,
					_endTime,
					_isRecurring,
					_flexJob,
					_flexJobMinHours,
					JobEventRecurrenceRule: {
						RepeatType,
						RepeatsOn,
						EndType,
						EndsAfterOccurrence,
						EndsOn,
						WeekFrequency,
						CustomDates,
					},
					SupervisorContactId,
					SecondarySupervisorModel,
				},
			} = data);
		}
		let isValid = true;
		// Calculate the job duration
		let jobDuration = timeToHours(user, _endTime) - timeToHours(user, _startTime);
		// If the number is negative this means the End Time is 12 AM which equates to 0 so we must add 24 to compensate
		if (jobDuration < 0) jobDuration += 24;

		// For flex jobs if the min hours value is ("" || 0) or if the value is greater than the total hours that can be worked on the job, return false
		if (
			_flexJob &&
			(_flexJobMinHours === "" ||
				parseFloat(_flexJobMinHours) === 0 ||
				parseFloat(_flexJobMinHours) > jobDuration ||
				parseFloat(_flexJobMinHours) >= 24)
		)
			isValid = false;
		// If any of the basic required fields are not filled in, return false
		if (
			Name === "" ||
			LocationId === "" ||
			Details === "" ||
			(JobEventTypeId !== 6 && _startDate === "")
		)
			isValid = false;
		if (!user.isCoordinator() && (ProjectId === "" || SupervisorContactId === ""))
			isValid = false;
		// If job event type is not "Event" and start/end time are empty, return false
		if (JobEventTypeId !== 6 && (_startTime === "" || _endTime === "")) isValid = false;
		else if (
			(utils.isIE || utils.isDesktopSafari) &&
			JobEventTypeId !== 6 &&
			(_startTime.replace(/_/g, "").length < 5 || _endTime.replace(/_/g, "").length < 5)
		) {
			isValid = false;
		}
		// Custom date recurrence coverage
		if (_isRecurring && RepeatType === 5 && CustomDates.length === 0) isValid = false;
		else if (_isRecurring && RepeatType === 5) {
			CustomDates.forEach(date => {
				// Make sure the chosen date is after the job start date
				let startDate = new Date(`${_startDate}T00:00:00`);
				let customDate = new Date(`${date}T00:00:00`);
				if (utils.isDesktopSafari || utils.isIE) {
					startDate = new Date(`${_startDate} 00:00`);
					customDate = new Date(`${date} 00:00`);
				}

				const dateBadge = document.querySelector(`[data-cd="${date}"]`);
				if (dateBadge && startDate.getTime() > customDate.getTime()) {
					dateBadge.classList.add("text-danger");
					isValid = false;
				} else if (dateBadge) {
					dateBadge.classList.remove("text-danger");
				}
			});
		}
		// Check that days are selected if repeating weekly
		// If days are selected, make sure the day the job starts is part of the selection
		if (_isRecurring && RepeatType === 4 && RepeatsOn.length === 0) isValid = false;
		if (_isRecurring && RepeatType === 4 && WeekFrequency === "") isValid = false;
		else if (_isRecurring && RepeatType === 4 && RepeatsOn.length > 0) {
			const dayOfWeek = new Date(`${_startDate} 00:00:00`).getDay();
			const { shortName } = DAYS_OF_WEEK.find(item => dayOfWeek === item.value) || {};
			if (RepeatsOn.indexOf(shortName) === -1) {
				isValid = false;
			}
		}

		// Ensure date value is correct for iOS devices
		EndsOn = utils.isIOSDevice ? EndsOn.replace(/-/gi, "/") : EndsOn;

		// Scenarios to cover recurrence inputs
		if (
			_isRecurring &&
			EndType === "OCCURRENCE" &&
			(EndsAfterOccurrence === "" || parseInt(EndsAfterOccurrence, 10) < 2)
		)
			isValid = false;
		else if (
			_isRecurring &&
			EndType === "DATE" &&
			(EndsOn === "" || new Date(`${_startDate} 00:00:00`) > new Date(`${EndsOn} 00:00:00`))
		)
			isValid = false;
		// TODO: Add validity check for recurrence end date (if entered)

		// Secondary supervisor validation
		if (SecondarySupervisorModel.length > 0) {
			for (let i = 0; i < SecondarySupervisorModel.length; i++) {
				const supervisorId =
					SecondarySupervisorModel[i].SecondaryAgencySupervisorId.toString();
				// If no supervisor selected or same supervisor selected twice, the section is invalid
				if (
					supervisorId === "" ||
					SecondarySupervisorModel.filter(
						item => item.SecondaryAgencySupervisorId.toString() === supervisorId,
					).length > 1
				) {
					isValid = false;
					break;
				}
			}
		}
		validateSection("generalInfo", isValid);
		return isValid;
	};

	const editingNonEvent =
		jobEditingProperties._prevJobType && jobEditingProperties._prevJobType !== 6;

	return (
		<div
			id="general-info-container"
			data-test-id="jobs__general-info-container"
			className="container-fluid"
		>
			<div className={classNames("form-group", { "invalid-field": form.Name === "" })}>
				<label htmlFor="jobName">
					{lang.t("Job Name")}
					<span className="required-mark" />
				</label>
				<input
					name="jobName"
					id="jobName"
					className="form-control"
					value={form.Name}
					onChange={handleChange("Name")}
					maxLength="500"
					placeholder={lang.t("Summarize job in one sentence")}
					data-test-id="jobs__job-name"
				/>
			</div>
			<Choose>
				<When condition={jobEditingProperties._userOwnsJob}>
					<If condition={!isInvoicingFirstEnabled}>
						<div
							className={classNames("row form-group", {
								"invalid-field": form._clientId === "",
							})}
						>
							<div className="col-xs-9">
								<label>{lang.t("Client")}</label>
								<span className="required-mark" />
							</div>
							<div className="col-xs-3 text-right">
								<button
									type="button"
									id="open-add-client-modal"
									className="btn btn-link btn-blue-small"
									onClick={() => setShowAddClientModal(true)}
								>
									{`+ ${lang.t("New")}`}
								</button>
							</div>
							<ReactSelect
								id="client-id"
								className="col-xs-12"
								value={form._clientId}
								name="clientId"
								onChange={onClientChange}
								options={clients}
								isLoading={loadingClients}
								onMenuClose={validateForm}
								isClearable
								dataTestId="jobs__client-id"
							/>
						</div>
					</If>
					<If condition={!user.isCoordinator()}>
						<div
							className={classNames("row form-group", {
								"invalid-field": form.ProjectId === "",
							})}
						>
							<div className="col-xs-9">
								<label>
									{lang.t("Project")}
									<span className="required-mark" />
								</label>
							</div>
							<div className="col-xs-3 text-right">
								<button
									type="button"
									id="open-add-project-modal"
									data-test-id="jobs__open-add-project-modal"
									className="btn btn-link btn-blue-small"
									onClick={openQuickAddProjectModal}
									disabled={form._clientId === ""}
								>
									{`+ ${lang.t("New")}`}
								</button>
							</div>
							<ReactSelect
								id="project-id"
								className="col-xs-12"
								value={form.ProjectId}
								name="ProjectId"
								onMenuClose={validateForm}
								onChange={onProjectChange}
								options={projects}
								isLoading={loadingProjects}
								isClearable
								dataTestId="jobs__project-id"
							/>
						</div>
					</If>
					<div
						className={classNames("row form-group", {
							"invalid-field": form.LocationId === "",
						})}
					>
						<div className="col-xs-9">
							<label>
								{lang.t("Location")}
								<span className="required-mark" />
							</label>
						</div>
						<div className="col-xs-3 text-right">
							<button
								type="button"
								id="open-add-location-modal"
								data-test-id="jobs__open-add-location-modal"
								className="btn btn-link btn-blue-small"
								onClick={() => setShowLocationModal(true)}
								disabled={form.ProjectId === ""}
							>
								{`+ ${lang.t("New")}`}
							</button>
						</div>

						<ReactSelect
							id="location-id"
							className="col-xs-12"
							value={form.LocationId}
							name="LocationId"
							onChange={onLocationChange}
							onMenuClose={validateForm}
							components={{ Option: LocationOption }}
							options={locations}
							isLoading={loadingLocations}
							isClearable
							dataTestId="jobs__dropdown-location"
						/>
					</div>
				</When>
				<Otherwise>
					<div className="row">
						<div className="unowned-job">
							<label>{lang.t("Client")}</label>
							<div>{jobEditingProperties._clientName}</div>
						</div>
					</div>
					<If condition={!user.isCoordinator()}>
						<div className="row">
							<div className="unowned-job">
								<label>{lang.t("Project")}</label>
								<div>{jobEditingProperties._projectName}</div>
							</div>
						</div>
					</If>
					<div className="row">
						<div className="unowned-job">
							<label>{lang.t("Location")}</label>
							<div>{jobEditingProperties._locationName}</div>
						</div>
					</div>
				</Otherwise>
			</Choose>
			<div className="form-group">
				<label>{lang.t("What type of job is this_")}</label>
				<ReactSelect
					id="job-event-type-id"
					dataTestId="jobs__event-type-id"
					value={form.JobEventTypeId}
					name="JobEventTypeId"
					onMenuClose={validateForm}
					onChange={onJobEventTypeChange}
					options={
						editingNonEvent
							? jobEventTypes.filter(jobType => jobType.value !== 6)
							: jobEventTypes
					}
					isLoading={loadingJobEventTypes}
					isDisabled={
						!!jobEditingProperties.Id &&
						jobEditingProperties._prevJobType === FORM_JOB_TYPES.EVENT.value
					}
				/>
			</div>
			<div className={classNames("form-group", { "invalid-field": form.Details === "" })}>
				<label>
					{lang.t("Job Description")}
					<span className="required-mark" />
				</label>
				<textarea
					className="form-control"
					value={form.Details}
					name="jobDetails"
					id="jobDetails"
					onChange={handleChange("Details")}
					placeholder={`${lang.t("Describe what the officer will be doing_")} ${lang.t("Be sure to include any specific instructions of duties_")}`}
					rows={4}
					data-test-id="jobs__job-details"
				/>
			</div>
			{jobDescriptionAttachments && !isDraft && (
				<div className="form-group">
					<input
						type="file"
						accept=".pdf,.jpeg,.jpg,.png,"
						max={MAX_JOB_EVENT_ATTACHMENT_SIZE}
						className="hidden"
						ref={fileInputRef}
						onChange={handleFileSelection}
						/>
					<button
						id="job-event-attachment-button"
						data-test-id="jobs__job-event-attachment"
						type="button"
						className="btn btn-link flex flex-direction-row flex-gap-4 flex-align-items-center"
						onClick={handleFileInputClick}
					>
						<i className=" rkf rkf-Paperclip" />
						{attachments.length === 0 ? lang.t("Attach File") : lang.t("Replace File")}
					</button>
					{attachments.length > 0 && (
						<div className="mt-2">
							<ul className="list-unstyled">
								{attachments.map(attachment => (
									<AttachmentListItem
										key={attachment.id}
										attachment={attachment}
										onClickRemove={handleRemoveFile}
									/>
								))}
							</ul>
						</div>
					)}
				</div>
			)}
			<div className={classNames("form-group", { "invalid-field": form.Details === "" })}>
				<label>{lang.t("Job Tags")}</label>
				<div>
					<Choose>
						<When condition={assignedJobtags.length > 0 || changeTags}>
							<Choose>
								<When condition={changeTags}>
									<div className="row">
										<div className="col-xs-10">
											<SelectBadges
												id="select-tags"
												defaultOption={lang.t("Add Tag")}
												optionList={jobTags}
												storedOptionList={assignedJobtags}
												handleOnChange={addJobTag}
												handleOnDelete={onRemoveTag}
												editDone={() => setChangeTags(false)}
												dataTestId="jobs__select-tag"
											/>
										</div>
										<div className="col-xs-2">
											<button
												type="button"
												className="btn btn-link"
												onClick={() => setChangeTags(false)}
												data-test-id="jobs__done-tag-button"
											>
												{`${lang.t("Done")}`}
											</button>
										</div>
									</div>
								</When>
								<Otherwise>
									<div style={{ display: "flex" }}>
										<If condition={assignedJobtags.length > 0}>
											<ul className="list-unstyled option-list">
												<For
													each="item"
													of={assignedJobtags}
													index="index"
												>
													<li
														key={`list-item-${item.label}-${item.value}`}
													>
														<button
															type="button"
															className="btn btn-rounded"
															onClick={() => onRemoveTag(item.value)}
														>
															{item.label}
															<span className="badge">&#10005;</span>
														</button>
													</li>
												</For>
											</ul>
										</If>
										<button
											type="button"
											className="btn btn-link"
											onClick={() => setChangeTags(true)}
											data-test-id="jobs__add-tags-button"
										>
											{`+ ${lang.t("Add")}`}
										</button>
									</div>
								</Otherwise>
							</Choose>
						</When>
						<Otherwise>
							<button
								type="button"
								className={!changeTags ? "btn btn-link" : "hidden"}
								onClick={() => setChangeTags(true)}
								data-test-id="jobs__add-tag-button"
							>
								{`+ ${lang.t("Add")}`}
							</button>
						</Otherwise>
					</Choose>
				</div>
			</div>
			<If condition={form.JobEventTypeId !== FORM_JOB_TYPES.EVENT.value}>
				<DateSelector
					user={user}
					jobEditingProperties={jobEditingProperties}
					form={form}
					updateForm={updateForm}
					validateForm={validateForm}
				/>
			</If>
			<div className="group-box">
				<Choose>
					<When condition={jobEditingProperties._userOwnsJob}>
						<div className="row">
							<h4 className="col-xs-12">{lang.t("Job Contact")}</h4>
						</div>
						<div className="row form-group client-contact-row">
							<div className="col-xs-9">
								<label>{lang.t("Client Contact")}</label>
							</div>
							<div className="col-xs-3 text-right">
								<button
									type="button"
									className="btn btn-link open-add-contact-modal btn-blue-small"
									onClick={() => openQuickAddContactModal("contacts")}
									disabled={form._clientId === ""}
									data-test-id="jobs__open-add-contact-modal"
								>
									{`+ ${lang.t("New")}`}
								</button>
							</div>
							<ReactSelect
								id="client-supervisor-id"
								className="col-xs-12"
								value={form.ClientSupervisorContactId}
								name="clientSupervisorContactId"
								onChange={handleChange("ClientSupervisorContactId", "select")}
								options={clientContacts}
								isLoading={false}
								isClearable
								dataTestId="jobs__dropdown-client-supervisor"
							/>
						</div>
					</When>
					<Otherwise>
						<div className="row">
							<div className="unowned-job">
								<label>{lang.t("Client Contact")}</label>
								<div data-test-id="jobs__client-supervisor-name">{jobEditingProperties._clientSupervisorName}</div>
							</div>
						</div>
					</Otherwise>
				</Choose>
				<div className="row form-group">
					<div className="checkbox col-xs-12">
						<label htmlFor="showClientContactToOfficer">
							<input
								type="checkbox"
								name="showClientContactToOfficer"
								id="showClientContactToOfficer"
								data-test-id="jobs__show-client-contact-to-officer"
								checked={form.ShowClientContactToOfficer}
								onChange={handleChange("ShowClientContactToOfficer", "checkbox")}
							/>
							{lang.t("Show Client Contact to Officer")}
						</label>
					</div>
				</div>
				<If condition={!user.isCoordinator()}>
					<div className="row">
						<div className="col-xs-12">
							<label>
								Agency Supervisor
								<span className="required-mark" />
							</label>
						</div>
						<If condition={user.isLEA()}>
							<div className="checkbox col-xs-7 include-coord-checkbox-container">
								<If condition={user.IsStaffAdmin}>
									<label htmlFor="includeCoordinators">
										<input
											type="checkbox"
											name="includeCoordinators"
											id="includeCoordinators"
											data-test-id="jobs__include-coordinators"
											checked={form._includeCoordinators}
											onChange={onIncludeCoordinatorsChange}
										/>
										Include coordinators
									</label>
								</If>
							</div>
						</If>
						<div
							className={`${user.isLEA() ? "col-xs-2 col-sm-3" : "col-xs-9 col-sm-10"} text-right`}
						>
							<If condition={user.IsStaffAdmin}>
								<button
									type="button"
									className="btn btn-link open-add-contact-modal btn-blue-small"
									onClick={() => openQuickAddContactModal("agency")}
									data-test-id="jobs__open-add-contact-modal"
								>
									{`+ ${lang.t("New")}`}
								</button>
							</If>
						</div>
					</div>
					<div
						className={classNames("row form-group", {
							"invalid-field": form.SupervisorContactId === "",
						})}
					>
						<div className="col-xs-9 col-sm-10">
							<ReactSelect
								id="agency-supervisor-id"
								value={form.SupervisorContactId}
								name="SupervisorContactId"
								onChange={handleAgencySupervisorChange}
								onMenuClose={validateForm}
								options={agencyContacts}
								isLoading={false}
								isClearable
								dataTestId="jobs__dropdown-agency-supervisor"
							/>
						</div>
						<div className="col-xs-3 col-sm-2 text-center">
							<button
								type="button"
								id="add-additional-supervisor"
								data-test-id="jobs__add-additional-supervisor"
								aria-label="add additional supervisor"
								onClick={handleAddSecondarySupervisor}
								className="btn btn-link"
								disabled={form.SupervisorContactId === ""}
							>
								<i className="rkf rkf-2x rkf-plus-o text-primary" />
							</button>
						</div>
					</div>
				</If>
				<div
					className={
						form.SecondarySupervisorModel.length > 0
							? "secondary-supervisor-section"
							: ""
					}
				>
					<For
						each="item"
						index="index"
						of={form.SecondarySupervisorModel}
					>
						<SecondarySupervisor
							key={`secondarySupervisor_${item.Id}_${index}`}
							item={item}
							index={index}
							updateSecondarySupervisorModel={updateSecondarySupervisorModel}
							agencyContacts={agencyContacts}
							primarySupervisor={form.SupervisorContactId}
							SecondarySupervisorModel={form.SecondarySupervisorModel}
						/>
					</For>
				</div>
			</div>

			<div className="text-center continue-button-container">
				<button
					type="button"
					id="general-info-continue"
					data-test-id="jobs__general-info-continue"
					onClick={() => handleContinue()}
					className="btn btn-primary btn-big"
				>
					{lang.t("Continue")}
				</button>
			</div>
			<AddClientModal
				show={showAddClientModal}
				user={user}
				onCancel={() => setShowAddClientModal(false)}
				onSave={() => {
					setShowAddClientModal(false);
					getClients();
				}}
			/>
			<QuickAddProjectModal
				show={quickAddProjectModalShow}
				agencyContacts={agencyContacts}
				clientId={Number(form._clientId)}
				clientName={clientName}
				successCallback={getProjects}
				closeModal={closeQuickAddProjectModal}
			/>
			<JobRequestClientSearch
				ref={_jobRequestClientSearch}
				clients={clients}
				jobRequestProperties={jobRequestProperties}
				updateRequestProperties={updateRequestProperties}
				getClients={getClients}
				getContacts={getContacts}
				updateClientId={onClientChange}
			/>
			<LocationModal
				show={showLocationModal}
				onHide={() => setShowLocationModal(false)}
				title={lang.t("Add Location")}
				projectId={form.ProjectId}
				onSuccess={() => {
					setShowLocationModal(false);
					getLocations(form.ProjectId);
				}}
				jobRequestProperties={jobRequestProperties}
				user={user}
			/>
		</div>
	);
};

GeneralInformation.propTypes = {
	user: PropTypes.object,
	form: PropTypes.object.isRequired,
	jobEditingProperties: PropTypes.object.isRequired,
	jobRequestProperties: PropTypes.object,
	agencyContacts: PropTypes.array,
	clientContacts: PropTypes.array,
	loadingForm: PropTypes.bool,
	finishLoadingForm: PropTypes.func.isRequired,
	updateLocationCoordinates: PropTypes.func.isRequired,
	updateForm: PropTypes.func.isRequired,
	updateRequestProperties: PropTypes.func.isRequired,
	expandNextSection: PropTypes.func.isRequired,
	validateSection: PropTypes.func.isRequired,
	getContacts: PropTypes.func.isRequired,
	openQuickAddContactModal: PropTypes.func.isRequired,
	getClients: PropTypes.func.isRequired,
	clients: PropTypes.array,
	loadingClients: PropTypes.bool,
	isInvoicingFirstEnabled: PropTypes.bool,
	projects: PropTypes.array,
	locations: PropTypes.array,
	loadingProjects: PropTypes.bool,
	loadingLocations: PropTypes.bool,
	isNewJob: PropTypes.bool,
	getProjects: PropTypes.func.isRequired,
	getLocations: PropTypes.func.isRequired,
	setLocations: PropTypes.func.isRequired,
	onClientChange: PropTypes.func.isRequired,
	onJobEventTypeChange: PropTypes.func.isRequired,
	flags: PropTypes.object,
	location: PropTypes.object.isRequired,
};

GeneralInformation.defaultProps = {
	user: {},
	jobRequestProperties: {},
	agencyContacts: [],
	clientContacts: [],
	loadingForm: false,
	clients: [],
	loadingClients: false,
	isInvoicingFirstEnabled: false,
	projects: [],
	locations: [],
	loadingProjects: false,
	loadingLocations: false,
	isNewJob: false,
	flags: {},
};

export default withLDConsumer()(GeneralInformation);
